import React, { useMemo, useState } from 'react';
import { apiRequest } from '../../../services/api/axiosConfig';
import { Product } from '../../../types/product';
import { DcmFunnelStageId, DcmSegmentMaster, DcmViewModel, DcmWho } from '../../../types/knowledge';
import { mergeDcmIntoMetadata, productToDcmViewModel } from '../../../utils/knowledgeDcmAdapter';
import PersonaDrawer from '../components/PersonaDrawer';
import PersonaGenerateModal from '../components/PersonaGenerateModal';
import SegmentEditModal from '../components/SegmentEditModal';
import SegmentGenerateModal from '../components/SegmentGenerateModal';
import StrategyMatrix from '../components/StrategyMatrix';

interface StrategyMapTabProps {
  product?: Product | null;
  onProductUpdate?: () => void;
}

const StrategyMapTab: React.FC<StrategyMapTabProps> = ({ product, onProductUpdate }) => {
  const initialViewModel = useMemo(() => productToDcmViewModel(product), [product]);
  const [viewModel, setViewModel] = useState<DcmViewModel>(initialViewModel);
  const [selectedWho, setSelectedWho] = useState<DcmWho | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSegmentModalOpen, setIsSegmentModalOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<DcmSegmentMaster | null>(null);
  const [personaTarget, setPersonaTarget] = useState<{
    segmentId: string;
    funnelStageId: DcmFunnelStageId;
  } | null>(null);

  // 商品が切り替わった時だけ完全リセットする。
  // 同一商品の保存後 refetch（onProductUpdate 経由で product 参照が変わる）では
  // ローカルの楽観的 viewModel と選択中ペルソナ（ドロワー）を維持する。
  // これをしないと、追加/保存のたびに selectedWho が null 化してドロワーが閉じる。
  const productIdRef = React.useRef<number | undefined>(product?.id);
  React.useEffect(() => {
    if (product?.id !== productIdRef.current) {
      productIdRef.current = product?.id;
      setViewModel(initialViewModel);
      setSelectedWho(null);
    }
  }, [product?.id, initialViewModel]);

  const saveViewModel = async (nextViewModel: DcmViewModel) => {
    if (!product) return;
    setIsSaving(true);
    setMessage(null);

    try {
      // 共通 axios（認証/ベースURL/エラー正規化を一元化。生fetchのURL・トークン不整合を解消）
      const metadata = mergeDcmIntoMetadata(product.extended_metadata, nextViewModel);
      await apiRequest.put(`knowledge/products/${product.id}`, { extended_metadata: metadata });

      setMessage({ type: 'success', text: '戦略マップを保存しました' });
      onProductUpdate?.();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || error?.message || '戦略マップの保存に失敗しました',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateAndSave = (updater: (current: DcmViewModel) => DcmViewModel) => {
    const nextViewModel = updater(viewModel);
    setViewModel(nextViewModel);
    saveViewModel(nextViewModel);
  };

  const handleCreateSegment = (segment: DcmSegmentMaster) => {
    updateAndSave((current) => ({
      ...current,
      segmentMaster: [...current.segmentMaster, segment],
    }));
  };

  const handleUpdateSegment = (next: DcmSegmentMaster) => {
    updateAndSave((current) => ({
      ...current,
      segmentMaster: current.segmentMaster.map((segment) =>
        segment.segment_id === next.segment_id ? next : segment,
      ),
    }));
    setEditingSegment(null);
  };

  // セグメントを消すときは配下のペルソナも一緒に消す。残すと mergeDcmIntoMetadata で
  // 存在しないセグメントに紐づいたまま fallbackSegment に落ちて幽霊化する。
  const handleDeleteSegment = (segmentId: string) => {
    updateAndSave((current) => ({
      ...current,
      segmentMaster: current.segmentMaster.filter((segment) => segment.segment_id !== segmentId),
      who: current.who.filter((item) => item.segment_id !== segmentId),
    }));
    if (selectedWho?.segment_id === segmentId) {
      setSelectedWho(null);
    }
    setEditingSegment(null);
  };

  const handleCreatePersona = (who: DcmWho) => {
    updateAndSave((current) => ({
      ...current,
      who: [...current.who, who],
    }));
    setSelectedWho(who);
  };

  const handleSaveWho = (nextWho: DcmWho) => {
    updateAndSave((current) => ({
      ...current,
      who: current.who.map((item) => (item.who_id === nextWho.who_id ? nextWho : item)),
    }));
    setSelectedWho(nextWho);
  };

  const handleDeleteWho = (whoId: string) => {
    updateAndSave((current) => ({
      ...current,
      who: current.who.filter((item) => item.who_id !== whoId),
    }));
    setSelectedWho(null);
  };

  if (!product) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-500">
        商品を選択してください
      </div>
    );
  }

  return (
    <div className="font-sans text-dark">
      {/* DCMモック準拠の見出し（保存は自動。セグメント追加はマトリクス右端の＋列から） */}
      <div className="mt-2 flex items-end justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold text-dark">
          <span className="material-icons-round text-[21px] text-primary">grid_view</span>
          戦略マップ
          <span className="help-tip">
            <span className="material-icons-round">help_outline</span>
            <span className="tip-body">
              <b>戦略マップ。</b>縦軸＝<b>ファネル</b>（認知・検討の段階）、横軸＝<b>セグメント</b>（顧客群）。
              その交点が <b>ペルソナ</b>で、各ペルソナの下に <b>訴求</b>が並びます。
              ペルソナか訴求名を選ぶと、右にパネルが開きます。検証は訴求ごとに回せます。
            </span>
          </span>
        </h2>
        {isSaving && <span className="text-xs text-gray-400">保存中...</span>}
      </div>

      {message && message.type === 'error' && (
        <div className="mt-3 rounded-tasky-sm bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {message.text}
        </div>
      )}

      <div className="mt-3">
        <StrategyMatrix
          segments={viewModel.segmentMaster}
          who={viewModel.who}
          selectedWhoId={selectedWho?.who_id ?? null}
          onSelectWho={setSelectedWho}
          onAddPersona={(segmentId, funnelStageId) => setPersonaTarget({ segmentId, funnelStageId })}
          onAddSegment={() => setIsSegmentModalOpen(true)}
          onEditSegment={setEditingSegment}
        />
      </div>

      <PersonaDrawer
        who={selectedWho}
        productId={product.id}
        segments={viewModel.segmentMaster}
        onClose={() => setSelectedWho(null)}
        onSave={handleSaveWho}
        onDelete={handleDeleteWho}
      />

      <SegmentEditModal
        segment={editingSegment}
        personaCount={
          viewModel.who.filter((item) => item.segment_id === editingSegment?.segment_id).length
        }
        onClose={() => setEditingSegment(null)}
        onSave={handleUpdateSegment}
        onDelete={handleDeleteSegment}
      />

      <SegmentGenerateModal
        isOpen={isSegmentModalOpen}
        productId={product.id}
        nextOrder={viewModel.segmentMaster.length}
        onClose={() => setIsSegmentModalOpen(false)}
        onCreate={handleCreateSegment}
      />

      {personaTarget && (
        <PersonaGenerateModal
          isOpen={Boolean(personaTarget)}
          productId={product.id}
          segmentId={personaTarget.segmentId}
          segmentName={
            viewModel.segmentMaster.find((segment) => segment.segment_id === personaTarget.segmentId)?.segment_name ||
            'セグメント'
          }
          funnelStageId={personaTarget.funnelStageId}
          onClose={() => setPersonaTarget(null)}
          onCreate={handleCreatePersona}
        />
      )}
    </div>
  );
};

export default StrategyMapTab;
